import React, { createContext, ReactNode, useCallback, useContext, useEffect, useRef, useState } from "react";
import { useLocation } from "react-router-dom";
import { Loader } from "../modules/common/modals/LoaderModal";

// Covers a specific gap that a component's own local loading state can't: when a component
// calls navigate() and then unmounts as part of that route change, its own loading UI (e.g. a
// submit button spinner) unmounts with it. If the destination route/page takes a moment to
// render anything meaningful, the user sees a blank page in between. This provider sits above
// the router's <Suspense>/<Routes> tree (see AppRoutes.tsx) so it survives route unmounts, and
// clears itself automatically on the next location change - callers don't need to (and can't
// reliably) clear it themselves once they've navigated away.
//
// This is deliberately NOT a replacement for local loading state (button spinners, disabled
// inputs, etc.) - use those for the normal "still mounted, waiting on a request" case. Reach for
// this only right before a navigate() call that might outlive the calling component.

const SAFETY_TIMEOUT_MS = 8000;

interface GlobalLoaderContextType {
  showGlobalLoader: (text?: string) => void;
  hideGlobalLoader: () => void;
}

const GlobalLoaderContext = createContext<GlobalLoaderContextType | undefined>(undefined);

export function GlobalLoaderContextProvider({ children }: { children: ReactNode }) {
  const [isVisible, setIsVisible] = useState(false);
  const [text, setText] = useState<string | undefined>(undefined);
  const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const location = useLocation();

  const hideGlobalLoader = useCallback(() => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }
    setIsVisible(false);
  }, []);

  const showGlobalLoader = useCallback((nextText?: string) => {
    setText(nextText);
    setIsVisible(true);

    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }

    timeoutRef.current = setTimeout(() => setIsVisible(false), SAFETY_TIMEOUT_MS);
  }, []);

  useEffect(() => {
    hideGlobalLoader();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname]);

  useEffect(() => {
    return () => {
      if (timeoutRef.current) clearTimeout(timeoutRef.current);
    };
  }, []);

  return (
    <GlobalLoaderContext.Provider value={{ showGlobalLoader, hideGlobalLoader }}>
      {children}
      {isVisible && <Loader text={text} />}
    </GlobalLoaderContext.Provider>
  );
}

export function useGlobalLoader() {
  const context = useContext(GlobalLoaderContext);
  if (!context) {
    // Fallback no-op so a missing provider fails soft (no overlay) rather than crashing.
    return { showGlobalLoader: () => {}, hideGlobalLoader: () => {} };
  }
  return context;
}
